import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { motion } from 'framer-motion';
import InputWithLabel from '../Components/InputComponent/InputWithLabel';
import SelectWithLabelOption from '../Components/SelectComponent/SelectWithLabelOption';
import HeadingTag from '../Components/HeadingComponent/HeadingTag';
import TextAreaWithLabel from '../Components/TextAreaWithLabel/TextAreaWithLabel';
import Seo from '../../components/SeoComponent/Seo';
import Loader from '../../components/Loader/Loader';
import Button from '../Components/ButtonComponent/Button';
import { showErrorToast, showSuccessToast } from '../../utils/toastUtils';
import { AddBannerService, deleteBannerService, fetchBanner } from '../../services/BannerService/Banner';
import ReportHeading from '../Components/ReportHeadings/ReportHeadings';
import BannerEditModal from './BannerEditModal';
import { FaEdit, FaTrash } from "react-icons/fa";
import ConfirmPopUp from '../../components/Dialog/ConfirmPopup';
import PaginationData from '../../components/PaginationData';

const Banner = () => {
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [paginationData, setPaginationData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [bannerData, setBannerData] = useState([]);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [formData, setFormData] = useState({
    banner_file: null,
    banner_type: '',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  function handleSetPage(page){
    setCurrentPage(page);
}
  useEffect(() => {
    loadBanner();
  }, [currentPage]);

  const loadBanner = async () => {
    setIsLoading(true);
    try {
      const response = await fetchBanner(currentPage);

      setBannerData(response?.data || []);
      setPaginationData(response.pagination)
    } catch (error) {
      console.error('Error Fetching Banner:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validate = () => {
    let validationErrors = {};
    if (!formData.banner_file) validationErrors.banner_file = 'Please upload a banner file.';
    if (!formData.banner_type) validationErrors.banner_type = 'Please select a banner type.';
    if (!formData.description) validationErrors.description = 'Please enter a description.';
    return validationErrors;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, banner_file: file });
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "banner_file") {
      setFormData({ ...formData, [name]: files[0] }); // Store the file object
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };


  const handleSubmit = async () => {
    const validationErrors = validate(formData);

    if (isEdited===false && Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("banner_file", formData.banner_file);
    formDataToSend.append("banner_type", formData.banner_type);
    formDataToSend.append("description", formData.description);

    try {
      if(isEdited===true){
        formDataToSend.append('banner_id',selectedId);
      }
      setErrors({})
      const response = await AddBannerService(formDataToSend,isEdited);
      if (response?.status.code === 201 || response?.status.code === 200) {

        showSuccessToast(response.status.message);
        loadBanner();
        setIsEdited(false);
        setSelectedId(null);
        setFormData({ banner_file: "", banner_type: "", description: "" });
      } else {
        showErrorToast(response?.data?.error);
      }
    } catch (error) {
      console.error("Error adding Banner:", error);
      showErrorToast("Failed to Add Banner.");
    }
  };

  async function handleDeleteBanner() {
    try {
      setIsLoading(true);
      let response = await deleteBannerService(selectedId);
      setIsLoading(false);
      if (response.status.code === 200) {
        setIsConfirm(false);
        setIsEdited(false);
        setSelectedId(null);
        setFormData({ banner_file: "", banner_type: "", description: "" });
        showSuccessToast("Banner Deleted successfully")
        loadBanner();


      } else {
        showErrorToast(response.message)
      }

    } catch (error) {
      setIsLoading(false);
      showErrorToast('An Error occur while delete banner.')
    }
  }
  const openConfirmPopup = (id) => {
    setSelectedId(id)
    setIsConfirm(true);
    if (isConfirm) return;
  }
  function handleEdit(item) {
    setSelectedId(item.id);
    setIsEdited(true);
    setFormData({
      banner_type:item.banner_type,
      banner_file:item.banner_file,
      description:item.description
    })
 
  }

  const handleUpdate = () => {
    loadBanner(); // Refresh table data
  };


  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      {isLoading && <Loader />}
      <Seo />
      <div className="mt-5 bg-white p-4">
        <ReportHeading name="Banner" />

        {/* Form Section */}
        <div className="flex flex-col md:flex-row justify-center gap-2">
          <InputWithLabel
            DivclassName="w-full md:w-1/4"
            label="Select Banner File"
            type="file"
            name="banner_file"
            onChange={handleFileChange}
            error={errors.banner_file}
          />
          <SelectWithLabelOption
            DivclassName="w-full md:w-1/4"
            label="Banner Type"
            name="banner_type"
            value={formData.banner_type}
            onChange={handleChange}
            error={errors.banner_type}
          >
            <option value="">Select Banner Type</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
          </SelectWithLabelOption>
          <TextAreaWithLabel
            DivclassName="w-full md:w-1/4"
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={errors.description}
          />
        </div>

        <div className="flex justify-center mt-4">
          <Button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>

        {/* Report Section */}
        <HeadingTag className="text-2xl text-blue-800 font-bold text-center mt-6" text="Report" />
        <div className="overflow-x-auto my-2 p-2">

          <ConfirmPopUp visible={isConfirm} onClose={() => setIsConfirm(false)} onConfirm={() => handleDeleteBanner()} title={'Are You sure want to delete banner?'} />


          <div className="container mx-auto p-1">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="text-md">
                  <tr className="bg-blue-800 text-white text-center">
                    <th className="border-2 p-2">S No.</th>
                    <th className="border-2">Banner File</th>
                    <th className="border-2">Banner Type</th>
                    <th className="border-2">Description</th>
                    <th className="border-2">Edit</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {bannerData?.length > 0 ? (
                    bannerData.map((item, index) => (
                      <tr
                        key={(currentPage - 1) * (paginationData?.per_page || 0) + index + 1}
                      >
                        <td className="border shadow-inner p-1">
                          {(currentPage - 1) * (paginationData?.per_page || 0) + index + 1}
                        </td>
                        <td className="border shadow-inner p-1 flex justify-center">
                          <img src={item.image_blob.original.dataUri} alt="" className='h-12' />
                        </td>
                        <td className="border shadow-inner p-1">
                          {item?.banner_type}
                        </td>
                        <td className="border shadow-inner p-1">
                          {item?.description}
                        </td>
                        <td className="border shadow-inner p-1">
                          <div className="flex justify-center gap-2">
                            <FaEdit
                              size={20}
                             onClick={() => handleEdit(item)}
                              className="text-blue-700 hover:bg-white cursor-pointer"
                            />
                            <FaTrash
                              size={20}
                              onClick={() => openConfirmPopup(item?.id)}
                              className="text-red-700 hover:bg-white cursor-pointer"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-2 text-center text-xl">
                        Data Not Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {isModalOpen && (
              <BannerEditModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                bannerId={selectedId}
                onUpdate={handleUpdate}
              />
            )}
            <div className="gap-2 mt-3">
              {/* <div className="flex justify-center gap-3">
                <button
                  className="py-1 text-xl rounded-md hover:bg-blue-800 px-4 bg-blue-500 text-white border-none cursor-pointer"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <div className="flex justify-end">
                  <p className="text-[17px] font-bold">
                    Page {currentPage} of {paginationData?.last_page || 1}
                  </p>
                </div>
                <button
                  className="py-1 text-xl rounded-md hover:bg-blue-800 px-4 bg-blue-900 text-white border-none cursor-pointer"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(prev + 1, paginationData?.last_page || 1)
                    )
                  }
                  disabled={currentPage === paginationData?.last_page}
                >
                  Next
                </button>

              </div> */}
                 <div className='flex justify-center my-2'>
                    <PaginationData currentPage={currentPage} lastPage={paginationData?.last_page} handlePage={handleSetPage} />
                </div>
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  );
};

export default Banner;
